import React, { useState, useEffect } from 'react';
import { X, Check, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'ostivex_cookie_consent';

export function CookieBanner({ lang = 'it' }) {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const save = async (analytics, marketing) => {
    const consent = { necessary: true, analytics, marketing, saved_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setVisible(false);

    // Registra consenso nel backend
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API}/api/gdpr/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'anonymous@cookie', necessary: true, analytics, marketing, source: 'cookie_banner' }),
      });
    } catch { /* best effort */ }
  };

  if (!visible) return null;

  const it = lang === 'it';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4" style={{ pointerEvents: 'none' }}>
      <div className="max-w-3xl mx-auto rounded-sm shadow-2xl" style={{ background: '#0e0e0e', border: '1px solid rgba(201,168,76,0.25)', pointerEvents: 'all' }}>
        {!showDetails ? (
          /* Banner compatto */
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5">
            <div className="flex-1">
              <p className="text-sm font-medium mb-1" style={{ color: '#e5e5e5' }}>
                {it ? '🍪 Utilizziamo i cookie' : '🍪 We use cookies'}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: '#888' }}>
                {it
                  ? 'Cookie necessari al funzionamento e cookie opzionali per analisi e marketing. Puoi scegliere quali accettare.'
                  : 'Essential cookies for functionality and optional cookies for analytics and marketing. You can choose which to accept.'}
                {' '}
                <a href="/privacy" className="underline" style={{ color: '#c9a84c' }}>
                  {it ? 'Privacy Policy' : 'Privacy Policy'}
                </a>
                {' · '}
                <a href="/cookie" className="underline" style={{ color: '#c9a84c' }}>
                  {it ? 'Cookie Policy' : 'Cookie Policy'}
                </a>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button onClick={() => setShowDetails(true)}
                className="px-3 py-2 text-xs rounded-sm"
                style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#888' }}>
                {it ? 'Personalizza' : 'Customize'} <ChevronRight size={10} className="inline" />
              </button>
              <button onClick={() => save(false, false)}
                className="px-3 py-2 text-xs rounded-sm transition-opacity hover:opacity-80"
                style={{ border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(229,229,229,0.7)', background: 'rgba(255,255,255,0.04)' }}>
                {it ? 'Solo necessari' : 'Necessary only'}
              </button>
              <button onClick={() => save(true, true)}
                className="px-4 py-2 text-xs font-semibold rounded-sm"
                style={{ background: '#c9a84c', color: '#000' }}>
                {it ? 'Accetta tutti' : 'Accept all'}
              </button>
            </div>
          </div>
        ) : (
          /* Pannello dettaglio */
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg font-light" style={{ color: '#e5e5e5' }}>
                {it ? 'Preferenze Cookie' : 'Cookie Preferences'}
              </h3>
              <button onClick={() => setShowDetails(false)}><X size={18} style={{ color: '#888' }} /></button>
            </div>

            {[
              {
                key: 'necessary', label: it ? 'Necessari' : 'Necessary', required: true,
                desc: it ? 'Essenziali per il funzionamento del sito. Non possono essere disabilitati.'
                  : 'Essential for site functionality. Cannot be disabled.',
              },
              {
                key: 'analytics', label: it ? 'Analitici' : 'Analytics', required: false,
                desc: it ? 'Ci aiutano a capire come usi il sito per migliorarlo.'
                  : 'Help us understand how you use the site to improve it.',
              },
              {
                key: 'marketing', label: it ? 'Marketing' : 'Marketing', required: false,
                desc: it ? 'Permettono di mostrarti contenuti personalizzati e offerte.'
                  : 'Allow us to show personalized content and offers.',
              },
            ].map(({ key, label, required, desc }) => (
              <div key={key} className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-0.5" style={{ color: '#ccc' }}>{label}</p>
                  <p className="text-xs" style={{ color: '#666' }}>{desc}</p>
                </div>
                <div>
                  {required ? (
                    <div className="w-8 h-4 rounded-full flex items-center justify-center" style={{ background: '#c9a84c' }}>
                      <Check size={9} color="#000" />
                    </div>
                  ) : (
                    <button onClick={() => setPrefs(p => ({ ...p, [key]: !p[key] }))}
                      className="w-10 h-5 rounded-full relative"
                      style={{ background: prefs[key] ? '#c9a84c' : '#2a2a2a' }}>
                      <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white"
                        style={{ left: prefs[key] ? '22px' : '2px', transition: 'left 0.15s' }} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              <button onClick={() => save(prefs.analytics, prefs.marketing)}
                className="flex-1 py-2.5 text-sm font-semibold rounded-sm"
                style={{ background: '#c9a84c', color: '#000' }}>
                {it ? 'Salva preferenze' : 'Save preferences'}
              </button>
              <button onClick={() => save(true, true)}
                className="px-4 py-2.5 text-xs rounded-sm"
                style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
                {it ? 'Accetta tutti' : 'Accept all'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
